import React, { useState, useEffect } from 'react';
import { Users, CreditCard, Pill, TrendingUp } from 'lucide-react';
import api from '../lib/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPharmacies: 0,
    pendingPayments: 0,
    totalMedicines: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [pharmaciesRes, transactionsRes] = await Promise.all([
          api.get(`/api/admin/pharmacies`),
          api.get(`/api/admin/transactions`)
        ]);

        const pharmacies = pharmaciesRes.data.data;
        const transactions = transactionsRes.data.data;

        const pendingCount = transactions.filter(t => t.status === 'PENDING').length;
        const totalRev = transactions
          .filter(t => t.status === 'SUCCESS')
          .reduce((acc, t) => acc + (t.amountBdt || 0), 0);

        setStats({
          totalPharmacies: pharmacies.length,
          pendingPayments: pendingCount,
          totalMedicines: 15000,
          totalRevenue: totalRev
        });
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="space-y-8 sm:space-y-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        <StatCard
          icon={<Users className="text-primary-600" />}
          title="Total Pharmacies"
          value={stats.totalPharmacies}
          color="bg-primary-50"
        />
        <StatCard
          icon={<CreditCard className="text-amber-600" />}
          title="Pending Payments"
          value={stats.pendingPayments}
          color="bg-amber-50"
        />
        <StatCard
          icon={<TrendingUp className="text-green-600" />}
          title="Total Revenue"
          value={`৳${stats.totalRevenue}`}
          color="bg-green-50"
        />
        <StatCard
          icon={<Pill className="text-blue-600" />}
          title="Medicines in DB"
          value={`${stats.totalMedicines}+`}
          color="bg-blue-50"
        />
      </div>

      {/* System status */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">System Status</h3>
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-gray-600">Backend API: Online</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-gray-600">Database: Connected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value, color }) => (
  <div className="card flex items-center gap-4 transition-transform hover:scale-[1.02]">
    <div className={`p-3 sm:p-4 rounded-2xl ${color} flex-shrink-0`}>
      {React.cloneElement(icon, { size: 24 })}
    </div>
    <div className="min-w-0">
      <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider truncate">{title}</p>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{value}</h2>
    </div>
  </div>
);

export default Dashboard;
